import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Chip,
  TableBody,
  Tooltip
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LayoutInfo, UIModuleComparison } from '../../../state/Layouts';
import {
  getBusinessFriendlyFieldName,
  changeMovedAndModified,
  getDisplayTitle,
  getBusinessFriendlyFieldValue,
  detectModuleModification
} from './utils';
import { ChangeBadges } from './ChangeBadges';
import { SwapHoriz, Edit } from '@mui/icons-material';
import { useLocales } from '../../../hooks';
import { ModuleChange } from '../../../API';
import ObjectPreview from '../../shared/ObjectPreview';
import { TooltipTitleUiModuleChange } from './TooltipTitleUiModuleChange';

type LayoutComparisonModuleChangesProps = {
  moduleChanges: ModuleChange[];
  sourceLayout: LayoutInfo;
  targetLayout: LayoutInfo;
};

const useStyles = makeStyles()((theme) => ({
  changesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  changeCard: {
    marginBottom: theme.spacing(2)
  },
  moved: {
    color: theme.palette.info.main
  },
  modified: {
    color: theme.palette.warning.main
  },
  modifiedField: {
    display: 'block'
  },
  modifiedFieldValue: {
    maxWidth: '100px',
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom'
  }
}));

export function LayoutComparisonModuleChanges({
  moduleChanges,
  sourceLayout,
  targetLayout
}: LayoutComparisonModuleChangesProps): React.ReactElement | null {
  const { t } = useLocales();
  const { classes } = useStyles();

  if (!moduleChanges || moduleChanges.length === 0) return null;

  // Build position-by-position diff
  const sourceModules = sourceLayout?.uiModules || [];
  const targetModules = targetLayout?.uiModules || [];
  const maxPositions = Math.max(sourceModules.length, targetModules.length);

  // Create a map of changes by position for easier lookup
  const changesByOldPosition = new Map<number, ModuleChange>();
  const changesByNewPosition = new Map<number, ModuleChange>();

  moduleChanges.forEach((change) => {
    if (change.oldPosition !== undefined) {
      changesByOldPosition.set(change.oldPosition, change);
    }
    if (change.newPosition !== undefined) {
      changesByNewPosition.set(change.newPosition, change);
    }
  });

  const renderPositionRow = (position: number) => {
    const sourceModule = position < sourceModules.length ? sourceModules[position] : null;
    const targetModule = position < targetModules.length ? targetModules[position] : null;

    // Check if there's a change at this position
    const oldPositionChange = changesByOldPosition.get(position);
    const newPositionChange = changesByNewPosition.get(position);

    // Use API changes first, fallback to detection
    // eslint-disable-next-line max-len
    const sourceChange =
      oldPositionChange ||
      (sourceModule && !newPositionChange ? detectModuleModification(sourceModule, targetModule, position) : undefined);
    // eslint-disable-next-line max-len
    const targetChange =
      newPositionChange ||
      (targetModule && !oldPositionChange ? detectModuleModification(sourceModule, targetModule, position) : undefined);

    const renderModuleCell = (
      module: UIModuleComparison | null,
      change: ModuleChange | undefined | null,
      isTarget: boolean
    ) => {
      if (!module && !change) return <Typography color="textSecondary">—</Typography>;

      if (change) {
        // Check if this module was both moved and modified
        const wasMovedAndModified = changeMovedAndModified(change);

        return (
          <Box display="flex" alignContent="flex-start" flexDirection="column" gap={1}>
            {/* First line: Icon + Title + Badges */}
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">{getDisplayTitle(change.title, change.moduleType, module)}</Typography>
                <Chip label={t(`layouts.ui_module_${change.moduleType.toLowerCase()}`)} size="small" />
                <ObjectPreview object={module} title={t('layouts.ui_module')} size="small" />
              </Box>
              <ChangeBadges change={change} />
            </Box>

            {/* Second line: Blue arrow with "from pos x" for moved items */}
            {(change.changeType === 'MOVED' || wasMovedAndModified) && change.oldPosition !== undefined && (
              <Box display="flex" alignItems="center" gap={1} className={classes.moved}>
                <SwapHoriz fontSize="small" />
                <Typography variant="caption">
                  {t(
                    `layouts.${isTarget ? 'from' : 'to'}_pos`,
                    isTarget ? { pos: change.oldPosition + 1 } : { pos: (change.newPosition ?? 0) + 1 }
                  )}
                </Typography>
              </Box>
            )}

            {/* Third line and beyond: Show modification details from API fieldChanges */}
            {change.changeType === 'MODIFIED' && change.fieldChanges && (
              <Box display="flex" alignItems="center" gap={1} className={classes.modified}>
                <Edit fontSize="small" />
                <Box>
                  {change.fieldChanges.map((fieldChange, index: number) => (
                    <Tooltip
                      key={index}
                      title={
                        <TooltipTitleUiModuleChange
                          fieldName={fieldChange.fieldName}
                          oldValue={fieldChange.oldValue}
                          newValue={fieldChange.newValue}
                        />
                      }
                      placement="top"
                      arrow
                    >
                      <Typography key={index} variant="caption" className={classes.modifiedField}>
                        <span>• {getBusinessFriendlyFieldName(fieldChange.fieldName)}: </span>
                        <span className={classes.modifiedFieldValue}>
                          {getBusinessFriendlyFieldValue(fieldChange.oldValue)}
                        </span>
                        <span> → </span>
                        <span className={classes.modifiedFieldValue}>
                          {getBusinessFriendlyFieldValue(fieldChange.newValue)}
                        </span>
                      </Typography>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );
      }

      // No change - just show the module
      return (
        <Typography variant="body2">
          {getDisplayTitle(module?.title, module?.moduleType || 'Module', module)}
        </Typography>
      );
    };

    return (
      <TableRow key={position}>
        <TableCell sx={{ width: '5%', textAlign: 'center', fontWeight: 'bold' }}>{position + 1}</TableCell>
        <TableCell sx={{ width: '47.5%', verticalAlign: 'top' }}>
          {renderModuleCell(sourceModule, sourceChange, false)}
        </TableCell>
        <TableCell sx={{ width: '47.5%', verticalAlign: 'top' }}>
          {renderModuleCell(targetModule, targetChange, true)}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className={classes.changesContainer}>
      <Card className={classes.changeCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('layouts.module_changes')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('layouts.position')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('layouts.before_source')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('layouts.after_target')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{Array.from({ length: maxPositions }, (_, index) => renderPositionRow(index))}</TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </div>
  );
}
